import { useState, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { create, close, ellipsisVertical, ribbon } from 'ionicons/icons';
import type { SkillData } from '@/lib/api/hooks';

interface SkillCardProps {
  skill: SkillData;
  onEdit?: (skill: SkillData) => void;
  onDelete?: (skill: SkillData) => void;
  onViewDetail?: (skill: SkillData) => void;
}

const PROF_COLOR: Record<string, string> = {
  Expert: 'var(--bp-success, #22c55e)',
  Intermediate: 'var(--bp-blue-500, #3b82f6)',
  Beginner: 'var(--bp-text-muted, #94a3b8)',
};

const PROF_WIDTH: Record<string, string> = {
  Expert: '100%',
  Intermediate: '66%',
  Beginner: '33%',
};

export function SkillCard({ skill, onEdit, onDelete, onViewDetail }: SkillCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const color = PROF_COLOR[skill.proficiency] ?? PROF_COLOR.Beginner;

  return (
    <div
      style={{
        position: 'relative',
        padding: '12px 16px',
        background: 'var(--bp-surface-raised, #1a2332)',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.06)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
      }}
      onClick={() => onViewDetail?.(skill)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>{skill.name}</div>
          {skill.category && (
            <span style={{
              fontSize: '0.65rem', padding: '1px 6px', borderRadius: 8,
              background: 'rgba(255,255,255,0.06)', color: 'var(--bp-text-muted, #64748b)',
            }}>
              {skill.category}
            </span>
          )}
        </div>

        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
            aria-label="Skill actions"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--bp-text-muted, #64748b)', padding: 4,
            }}
          >
            <IonIcon icon={ellipsisVertical} />
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 10,
              background: 'var(--bp-surface, #1e293b)',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              minWidth: 120, overflow: 'hidden',
            }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit?.(skill); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                  color: 'var(--bp-text, #f1f5f9)', fontSize: '0.8rem', cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <IonIcon icon={create} style={{ fontSize: '0.9rem' }} /> Edit
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete?.(skill); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                  color: 'var(--bp-error, #ef4444)', fontSize: '0.8rem', cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <IonIcon icon={close} style={{ fontSize: '0.9rem' }} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div style={{
          height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 2, background: color,
            width: PROF_WIDTH[skill.proficiency] ?? '33%',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 4, fontSize: '0.7rem', color: 'var(--bp-text-muted, #64748b)',
        }}>
          <span style={{ color }}>{skill.proficiency}</span>
          {skill.yearsExperience != null && (
            <span>{skill.yearsExperience}yr{skill.yearsExperience !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {skill.endorsements != null && skill.endorsements > 0 && (
        <div style={{
          marginTop: 6, fontSize: '0.65rem',
          color: 'var(--bp-text-muted, #64748b)',
        }}>
          {skill.endorsements} endorsement{skill.endorsements !== 1 ? 's' : ''}
        </div>
      )}

      {skill.ofoCode && (
        <div style={{
          marginTop: 4, display: 'flex', alignItems: 'center', gap: 4,
          fontSize: '0.65rem', color: 'var(--bp-blue-400, #60a5fa)',
        }}>
          <IonIcon icon={ribbon} style={{ fontSize: '0.75rem' }} />
          OFO: {skill.ofoCode.id}
        </div>
      )}
    </div>
  );
}
